import { Margin } from '@mui/icons-material';
import { React, useState, useEffect, CloseIcon, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Grid2, Typography, Box, Default_Text_Color, formatNumberWithCommas } from '../../utils/globalImports';
import { CustomButton, CustomTextField, CustomRadioButton, validationMessages } from '../../utils/globalImports';
import { validateField, validationPatterns } from '../../utils/validationUtils';

interface ModalProps {
    open: boolean;  // Prop to determine if the modal is open
    onClose: (closeConfirmationRequired: boolean) => void;  // Function to handle modal close
    onSubmit: (formData: FeeStage) => void;
    feeStageData?: FeeStage;  // Optional prop for editing FeeStage
    handleContentChange: (state: boolean) => void;
}

interface FeeStage {
    refrenceNumber: string;
    "Task Ref"?: string; // Add this field explicitly
    description: string;
    value: string;
    valuePer: string;
    completionNotes: string;
    "Terms and Notes"?: string;
    status?: boolean;
    Status?: string;
}

const CustomAddFeeStageModal: React.FC<ModalProps> = ({ open, onClose, onSubmit, feeStageData, handleContentChange }) => {
    // Form data state
    const [formData, setFormData] = useState<FeeStage>({
        refrenceNumber: '',
        description: '',
        value: '',
        valuePer: '',
        completionNotes: '',
        status: true,
    });

    const [errors, setErrors] = useState({
        refrenceNumber: '',
        description: '',
        value: '',
        valuePer: '',
        completionNotes: ''
    });

    useEffect(() => {
        if (open) {
            if (feeStageData) {
                //const [Value, ValuePer] = feeStageData.value.split(' ');
                const match = feeStageData.value.match(/^(-?[£$€]\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s+(.*)$/);
                const Value = match?.[1] ?? "";    // e.g., -£1,000.00
                const ValuePer = match?.[2] ?? ""; // e.g., per Report

                setFormData({
                    refrenceNumber: feeStageData["Task Ref"] || '',
                    description: feeStageData.description || '',
                    value: Value.replace(/[^0-9.-]/g, "") || '',
                    valuePer: ValuePer?.trim() || '',
                    completionNotes: feeStageData["Terms and Notes"] || '',
                    status: feeStageData.Status == "Active" ? true : false,
                });
            } else {
                setFormData({
                    refrenceNumber: '',
                    description: '',
                    value: '',
                    valuePer: '',
                    completionNotes: '',
                    status: true,
                });
            }
            setErrors({
                refrenceNumber: '',
                description: '',
                value: '',
                valuePer: '',
                completionNotes: ''
            });
        }

    }, [open, feeStageData])



    // Handle form data change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        let cleanedValue = value;

        if (name === "value") {
            // Remove commas before storing
            cleanedValue = value.replace(/,/g, "");

            // Allow only numbers
            if (!/^\d*$/.test(cleanedValue)) return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]:
                type === "checkbox"
                    ? checked
                    : type === "radio"
                        ? value === "true"
                        : cleanedValue,
        }));
        handleContentChange(true);
    };


    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            onSubmit(formData);
            // onClose(); // Close modal after submit
        }
    };

    // validate form field
    const validate = () => {

        let isValid = true;
        const errors = { refrenceNumber: '', description: '', value: '', valuePer: '', completionNotes: '' };

        errors.refrenceNumber = validateField(formData.refrenceNumber, {
            fieldName: "Reference number",
            required: true,
            maxLength: 20,
            pattern: validationPatterns.alphanumericWithSpace,
        });

        if (errors.refrenceNumber) isValid = false;

        errors.description = validateField(formData.description, {
            fieldName: "Description",
            required: true,
            maxLength: 5000,
        });

        if (errors.description) isValid = false;

        errors.value = validateField(formData.value, {
            fieldName: "Value",
            required: true
        });

        if (errors.value) isValid = false;

        errors.valuePer = validateField(formData.valuePer, {
            fieldName: "Unit Description",
            required: true,
            pattern: validationPatterns.alpha,
        });

        if (errors.valuePer) isValid = false;

        errors.completionNotes = validateField(formData.completionNotes, {
            fieldName: "Terms and Notes",
            maxLength: 5000,

        });

        if (errors.completionNotes) isValid = false;

        setErrors(errors);
        return isValid;
    };

    return (
        <div>

            {/* Modal Dialog */}
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth >
                <DialogTitle
                    sx={{
                        position: 'relative',
                        color: Default_Text_Color,
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        mb: 0,
                        fontWeight: 500,
                        fontSize: '22px'
                    }}>
                    {feeStageData ? 'Edit Fee Stage' : 'Add Fee Stage'}
                    <IconButton
                        color="inherit"
                        onClick={() => onClose(true)}
                        sx={{
                            position: 'absolute',
                            right: 16, // Position the button at the right
                            top: 16,   // Position the button at the top
                            height: '22px',
                            width: '22px',
                            p: 2,
                            border: '1px solid #d7d7d7',
                            borderRadius: '8px'

                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider></Divider>

                <DialogContent>
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ mb: 1 }}>
                            <CustomTextField
                                label="Task Reference Number"
                                name="refrenceNumber"
                                value={formData.refrenceNumber}
                                onChange={handleChange}
                                sx={{ mb: 1 }}
                                required={true}
                            />
                            {errors.refrenceNumber && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.refrenceNumber}
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ mb: 1 }}>
                            <CustomTextField
                                label="Fee Stage Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                sx={{ mb: 1 }}
                                required={true}
                                isTextArea={true}
                            />
                            {errors.description && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.description}
                                </Typography>
                            )}
                        </Box>


                        <Grid2 sx={{ display: 'flex', gap: 5 }}>

                            <Box sx={{ mb: 1 }}>
                                <CustomTextField
                                    label="Value"
                                    name="value"
                                    type="text"
                                    //value={formData.value}
                                    value={
                                        formData.value
                                            ? formatNumberWithCommas(formData.value)
                                            : ""
                                    }
                                    onChange={handleChange}
                                    sx={{ mb: 1 }}
                                    required={true}
                                />
                                {errors.value && (
                                    <Typography className="validationMessage" role="alert">
                                        {errors.value}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ mb: 1 }}>
                                <CustomTextField
                                    label="Unit Description"
                                    name="valuePer"
                                    value={formData.valuePer}
                                    onChange={handleChange}
                                    sx={{ mb: 1 }}
                                    required={true}
                                />
                                {errors.valuePer && (
                                    <Typography className="validationMessage" role="alert">
                                        {errors.valuePer}
                                    </Typography>
                                )}
                            </Box>


                        </Grid2>

                        <CustomTextField
                            label="Terms and Notes"
                            name="completionNotes"
                            value={formData.completionNotes}
                            onChange={handleChange}
                            sx={{ mb: 1 }}
                            isTextArea={true}
                        />
                        {errors.completionNotes && (
                            <Typography className="validationMessage" role="alert">
                                {errors.completionNotes}
                            </Typography>
                        )}

                        {feeStageData &&
                            <>
                                {/* radio button */}
                                <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1 }}>Status</Typography>
                                <Grid2 sx={{ display: 'flex' }}> {/* Adjust gap as needed */}
                                    {/* Custom Radio Buttons */}
                                    <CustomRadioButton
                                        label="Active"
                                        value="true"
                                        name="status"
                                        checked={formData.status === true}
                                        onChange={handleChange}
                                        required={true}
                                    />
                                    <CustomRadioButton
                                        label="Inactive"
                                        value="false"
                                        name="status"
                                        checked={formData.status === false}
                                        onChange={handleChange}
                                        required={true}
                                    />
                                </Grid2>
                            </>
                        }
                    </form>

                </DialogContent>

                <Divider></Divider>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                    }}
                >
                    <CustomButton
                        capsule
                        onClick={() => onClose(true)}
                        sx={{
                            textTransform: 'none',
                            width: 'auto',
                            minWidth: 'fit-content',
                            flex: 'none',
                        }}
                    >
                        Cancel
                    </CustomButton>

                    <CustomButton
                        capsule
                        onClick={handleSubmit}
                        sx={{
                            textTransform: 'none',
                            width: 'auto',
                            minWidth: 'fit-content',
                            flex: 'none',
                        }}
                    >
                        {feeStageData ? 'Update Fee Stage' : 'Add Fee Stage'}
                    </CustomButton>
                </DialogActions>
            </Dialog>
        </div >
    );
};

export default CustomAddFeeStageModal;
